<template>
  <section class="section facilities-table-section">
    <h2 class="facilities-table-section__title">
      {{ $t("List of facilities (table)") }}
    </h2>
    <FacilitiesTableFilter />
    <div class="facilities-table-section__wrapper">
      <SpinnerRadial
        v-if="isLoadingData"
        class="facilities-table-section__spinner"
      />
      <p v-else-if="!facilities.length" class="facilities-table-section__no-result">
        {{ $t("Nothing found") }}
      </p>
      <template v-else>
        <FacilitiesTable
          v-for="facilitiesItem in facilities"
          :key="facilitiesItem.city"
          :facilities="facilitiesItem"
          @select-facility="openFacilityModal($event)"
        />
      </template>
    </div>
    <LazyModalsFacilityObjectModal
      v-if="facilityId"
      class="facilities-table-section__object-modal"
      :facility-object-id="facilityId"
      :selected-level="preparedSelectedLevel"
      :subscription-model="subscriptionModel"
      :tab-options="subscriptionModelTabOptions"
      @close="closeFacilityModal"
    />
  </section>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import FacilitiesTableFilter from "~/components/facilities/FacilitiesTableFilter.vue";
import FacilitiesTable from "~/components/facilities/FacilitiesTable.vue";
import SpinnerRadial from "~/components/loaders/SpinnerRadial.vue";
import { LazyModalsFacilityObjectModal } from "#components";
import { getTableFacilities, getTableFilters } from "~/api/table";
import { PAYLOAD_TABLE_LEVELS, useTableFilterStore } from "~/stores/tableFilter";
import { useCountriesStore } from "~/stores/countries";

const tableFilterStore = useTableFilterStore();
const countryStore = useCountriesStore();
const {
  search,
  availableLevelsByTypeGetter,
  selectedLevels,
  translatedActivities,
  selectedTags,
  selectedActivities,
  isLoadingData,
  facilities,
  selectedCity,
  subscriptionModel,
  subscriptionModelTabOptions,
} = storeToRefs(tableFilterStore);

const { currentCountry } = storeToRefs(countryStore);

const {
  getSelectedLevelIds,
  setAvailableLevels,
  setSelectedLevelsByType,
  setSelectedCities,
  setCitiesList,
  setTags,
  prepareSelectedActivities
} = tableFilterStore;

// `selectedLevels` are sorted in the store by priority and always exist,
// and we need to take the last one to get the highest level
const preparedSelectedLevel = computed(() => {
  return {
    archived: selectedLevels.value.archived.at(-1)!,
    default: selectedLevels.value.default.at(-1)!,
  };
});

const tableFilters = await getTableFilters({
  country: currentCountry.value.code,
  levels: PAYLOAD_TABLE_LEVELS[subscriptionModel.value],
  subscriptionModel: subscriptionModel.value,
});

const facilityId = ref<string | number | null>(null);
const openFacilityModal = (id: string | number): void => {
  facilityId.value = id;
};
const closeFacilityModal = (): void => {
  facilityId.value = null;
};

const applyTableFilters = (tableFilters: Awaited<ReturnType<typeof getTableFilters>>) => {
  translatedActivities.value = tableFilters.activities;
  setTags(tableFilters.tags);

  const availableTagIds = new Set(tableFilters.tags.map((tag) => tag.id));
  selectedTags.value = selectedTags.value.filter((tag) => availableTagIds.has(tag.id));
};

onMounted(async () => {
  if (tableFilters) {
    setAvailableLevels(tableFilters.levels);
    setSelectedLevelsByType(availableLevelsByTypeGetter.value);
    setCitiesList(tableFilters.cities);
    setSelectedCities(tableFilters.cities[0]);
    applyTableFilters(tableFilters);
  }
  try {
    facilities.value = await getTableFacilities({
      country: currentCountry.value.code,
      search: search.value,
      levels: getSelectedLevelIds(),
      city: selectedCity.value?.id,
      subscriptionModel: subscriptionModel.value,
      activities: prepareSelectedActivities(),
      tags: selectedTags.value.map(tag => tag.id) || [],
    });
  } catch (error) {
    if (error instanceof Error) {
      console.log(`Error: ${error.message}`);
    }
  }
  finally {
    isLoadingData.value = false;
  }
});

watch([selectedCity, selectedLevels, selectedActivities], async () => {
  const tableFilters = await getTableFilters({
    country: currentCountry.value.code,
    subscriptionModel: subscriptionModel.value,
    city: selectedCity.value?.id,
    levels: getSelectedLevelIds(),
    activities: prepareSelectedActivities(),
  });

  if (tableFilters) {
    applyTableFilters(tableFilters);
  }
}, { deep: true });
</script>
